import { Link } from 'react-router-dom'
import { FaExternalLinkAlt, FaChalkboardTeacher } from 'react-icons/fa'
import InlineHighlight from '../components/content/InlineHighlight/InlineHighlight'
import HeroArtGrid from '../components/sections/HeroArtGrid/HeroArtGrid'
import { HERO_ART } from '../config/heroArt'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'
import { DECK_GROUPS, DECK_COUNT, deckUrl } from '../config/decks'

/**
 * The index for hub.gazar.dev.
 *
 * Every deck already carries the newsletter CTA and the decks are already the
 * largest single referrer to `/newsletter`. Until this page existed there was no
 * route from the site to any of them, so the only people who ever saw one had
 * attended the lesson or been sent the link. This is the missing link, and
 * nothing more: no new asset, no new copy on the decks themselves.
 *
 * Attribution is by referrer rather than a tagged query string. The hub reads no
 * parameters, so a `?from=` would be decoration that implies a measurement
 * nobody takes.
 */
export default function DecksPage() {
  return (
    <div className="min-h-screen bg-gray-50 text-[#232323]">
      <section className="relative overflow-hidden bg-brand-navy py-20 md:py-28">
        <ContentColumn anchor="decks-hero" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
            <div className="max-w-3xl">
              <span className="mb-8 inline-flex w-fit items-center gap-2 font-body text-[15px] leading-[1.58] tracking-[-0.003em] text-gray-50/80">
                <FaChalkboardTeacher className="text-base" aria-hidden="true" />
                {DECK_COUNT} decks, free, no sign-up
              </span>

              <h1 className="mb-6 font-sans text-[42px] font-bold leading-[1.3] tracking-[-0.02em] text-gray-50 sm:text-[48px] md:text-[56px]">
                Every <InlineHighlight color="red">slide deck</InlineHighlight> I teach from
              </h1>

              <p className="font-body text-[21px] leading-[1.58] tracking-[-0.003em] text-gray-50/85">
                The lightning lessons, the full course decks, and the reference pages. All of them
                open in the browser, none of them behind an email form.
              </p>
            </div>
            <HeroArtGrid images={HERO_ART.lessons} />
          </div>
        </ContentColumn>
      </section>

      <section className="border-b border-border-subtle bg-gray-50">
        <div className="mx-auto w-full max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.18em] text-gray-900/60 sm:text-sm">
              <li>
                <Link
                  to="/"
                  className="transition-colors hover:text-gray-900 focus:text-gray-900 focus:outline-none"
                >
                  Home
                </Link>
              </li>
              <li className="font-heading text-gray-900">/</li>
              <li className="font-heading text-gray-900" aria-current="page">
                Decks
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {DECK_GROUPS.map((group, index) => (
        <section
          key={group.id}
          id={group.id}
          className={`scroll-mt-20 py-16 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        >
          <ContentColumn
            anchor={group.id}
            className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8"
          >
            <div className="mx-auto mb-12 max-w-3xl text-center">
              <h2 className="mb-4 font-heading text-3xl font-bold uppercase text-gray-900">
                <InlineHighlight color="navy">{group.title}</InlineHighlight>
              </h2>
              <p className="font-body text-sm text-ink-soft sm:text-base">{group.intro}</p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {group.decks.map((deck) => (
                <a
                  key={deck.slug}
                  href={deckUrl(deck.slug)}
                  // Opens on the hub, a different origin. `noopener` is not
                  // optional with `_blank`: without it the opened page gets a
                  // handle back to this one.
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-full flex-col rounded-smooth border border-border-strong bg-white p-6 transition-colors duration-150 hover:border-brand-navy focus:border-brand-navy focus:outline-none"
                >
                  <h3 className="mb-3 font-heading text-lg font-bold text-brand-navy">
                    {deck.title}
                  </h3>
                  <p className="mb-4 flex-1 font-body text-sm leading-relaxed text-ink-soft">
                    {deck.blurb}
                  </p>
                  <span className="inline-flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide text-brand-teal">
                    Open the deck
                    <FaExternalLinkAlt className="text-xs" aria-hidden="true" />
                  </span>
                </a>
              ))}
            </div>
          </ContentColumn>
        </section>
      ))}
    </div>
  )
}
